import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  VStack,
  List,
  ListItem,
  ListIcon,
  Text,
  Box,
  Icon
} from "@chakra-ui/react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { saveToken } from "utils/auth";
import { useHistory } from "react-router";
import { useUser } from "context/user";
import { Redirect } from "react-router-dom";
import { resetPassword, fetchResetIdentity } from "api/webservice/auth";
import { useToast } from "hooks";
import { Form, Input } from "components";
import {
  CATEGORY_RULES,
  MIN_LENGTH,
  REQUIRED_CATEGORY_COUNT,
  countCategories,
  containsIdentity,
  validatePassword,
  IdentityFields
} from "./passwordValidation";

type FormData = {
  password: string;
};

type RuleIndicatorProps = {
  label: string;
  met: boolean;
};

const RuleIndicator: React.FC<RuleIndicatorProps> = ({ label, met }) => (
  <ListItem
    fontSize="sm"
    color={met ? "green.500" : "gray.500"}
    display="flex"
    alignItems="center"
  >
    <ListIcon
      as={met ? AiOutlineCheckCircle : AiOutlineCloseCircle}
      color={met ? "green.500" : "gray.400"}
    />
    {label}
  </ListItem>
);

export const ResetPasswordForm: React.FC = () => {
  const history = useHistory();
  const { handleSubmit, errors, register, formState, watch } = useForm<
    FormData
  >();
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const token = urlParams.get("token");
  const { setUser } = useUser()!;
  const toast = useToast();

  // Server-side validation errors (e.g. password history reuse) returned by
  // the backend as { message, errors: [] }.
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  // Identity fields (username/name) for the reset token, fetched read-only to
  // drive the "must not contain your username or name" live-feedback rule.
  const [identity, setIdentity] = useState<IdentityFields>({});

  useEffect(() => {
    if (!token) return;
    fetchResetIdentity(token)
      .then(data => {
        setIdentity({
          username: data?.username,
          firstName: data?.first_name,
          lastName: data?.last_name
        });
      })
      .catch(() => {
        // Non-fatal: identity feedback is UX-only and the server still enforces
        // the rule. Leave identity empty so the client-side rule is skipped.
        setIdentity({});
      });
  }, [token]);

  if (!token) return <Redirect to="/login" />;

  const passwordValue = watch("password") ?? "";
  const categoriesMet = countCategories(passwordValue);
  const hasIdentity = !!passwordValue && containsIdentity(passwordValue, identity);

  const submitForm = async ({ password }: FormData): Promise<any | null> => {
    setServerErrors([]);

    await resetPassword(password, token)
      .then(response => {
        const token = response?.token ?? null;
        const profile = response?.profile ?? null;

        if (token && profile) {
          saveToken(token);
          setUser(profile);
          if (profile.privs > 0) {
            const adminPortalUrl =
              process.env.REACT_APP_ADMIN_PORTAL_URL ?? "/";
            window.location.href = adminPortalUrl;
          } else {
            history.replace("/dashboard");
          }
        } else {
          toast({
            title: "Unable to reset password",
            description: "Please try again.",
            status: "error"
          });
        }
      })
      .catch(error => {
        // The backend returns validation/history failures as HTTP 400 with a
        // { response: { message, errors: [] } } body. Other failures return a
        // plain string in `response`.
        const serverResponse = error?.response?.data?.response;

        if (serverResponse && typeof serverResponse === "object") {
          const messages: string[] = Array.isArray(serverResponse.errors)
            ? serverResponse.errors
            : [];
          setServerErrors(messages);
          toast({
            title: "Unable to reset password",
            description:
              serverResponse.message ?? "Please review the requirements below.",
            status: "error"
          });
        } else {
          toast({
            title: "Unable to reset password",
            description:
              typeof serverResponse === "string"
                ? serverResponse
                : "Please try again.",
            status: "error"
          });
        }
      });
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <VStack spacing={6} align="stretch">
        <Input
          name="password"
          label="Password"
          type="password"
          hideLabel
          register={register({
            validate: value => validatePassword(value, identity)
          })}
          placeholder="New password"
          inputIcon="password"
          errorMessage={errors.password?.message}
        />

        <Box>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            Password must meet the following requirements:
          </Text>
          <List spacing={1}>
            <RuleIndicator
              label={`At least ${MIN_LENGTH} characters`}
              met={passwordValue.length >= MIN_LENGTH}
            />
            <RuleIndicator
              label={`At least ${REQUIRED_CATEGORY_COUNT} of the following (${categoriesMet}/${CATEGORY_RULES.length}):`}
              met={categoriesMet >= REQUIRED_CATEGORY_COUNT}
            />
            <Box pl={6}>
              <List spacing={1}>
                {CATEGORY_RULES.map(rule => (
                  <RuleIndicator
                    key={rule.label}
                    label={rule.label}
                    met={rule.isMet(passwordValue)}
                  />
                ))}
              </List>
            </Box>
            <RuleIndicator
              label="Does not contain your username or name"
              met={!hasIdentity}
            />
          </List>
        </Box>

        {serverErrors.length > 0 && (
          <Box
            borderWidth="1px"
            borderColor="red.300"
            borderRadius="md"
            bg="red.50"
            p={3}
          >
            <List spacing={1}>
              {serverErrors.map(message => (
                <ListItem
                  key={message}
                  fontSize="sm"
                  color="red.600"
                  display="flex"
                  alignItems="center"
                >
                  <Icon as={AiOutlineCloseCircle} color="red.500" mr={2} />
                  {message}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Button
          disabled={formState.isSubmitting}
          type="submit"
          size="md"
          variant="blue"
        >
          Reset Password
        </Button>
      </VStack>
    </Form>
  );
};
